import React, { useEffect, useState } from "react";
import {
  AddedDefectEntry,
  Classification,
  ModuleDataInspection,
  SubmissionPayload,
} from "../../../types/Inspection";
import { ModuleSize } from "../../../types/ModuleSize";
import { visualDefectsData } from "./defects";
import AccordionWrapper from "./inspection/AccordionWrapper";
import Defects from "./inspection/Defects";
import Remark from "./inspection/Remark";
import Classifications from "./inspection/Classification";
import styled from "styled-components";

const classificationColors: { [key in Classification]: string } = {
  A: "green",
  B: "orange",
  C: "red",
  Q: "purple",
};

const ClassificationBadge = styled.span<{ classification: Classification }>`
  background-color: ${(props) => classificationColors[props.classification]};
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-weight: bold;
  font-size: 1rem;
`;

type VisualInspectionFormProps = {
  visualInspection?: ModuleDataInspection;
  onSubmit: (payload: SubmissionPayload) => void;
  initialIsOpen?: boolean;
  size: ModuleSize;
};

export const VisualInspectionForm: React.FC<VisualInspectionFormProps> = ({
  visualInspection,
  onSubmit,
  initialIsOpen = false,
  size,
}) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);
  const [remark, setRemark] = useState("");
  const [addedDefects, setAddedDefects] = useState<AddedDefectEntry[]>([]);
  const [classification, setClassification] = useState<Classification | null>(
    null
  );

  useEffect(() => {
    setClassification(visualInspection?.classification ?? null);
    setRemark(visualInspection?.remark ?? "");
    setAddedDefects(visualInspection?.defects ?? []);

    if (visualInspection) {
      onSubmit({
        classification: visualInspection.classification,
        remark: visualInspection.remark,
        defects: visualInspection.defects,
      });
    }
  }, [visualInspection]);

  const handleSubmit = (selectedClass: Classification) => {
    setClassification(selectedClass);

    const payload: SubmissionPayload = {
      classification: selectedClass,
      remark,
      defects: addedDefects.map(({ cells, defects }) => ({ cells, defects })),
    };

    onSubmit(payload);
    setIsOpen(false);
  };

  return (
    <AccordionWrapper
      header="Visual Inspection"
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      headerBadge={
        classification && (
          <ClassificationBadge classification={classification}>
            {classification}
          </ClassificationBadge>
        )
      }
    >
      <Defects
        addedDefects={addedDefects}
        setAddedDefects={setAddedDefects}
        defectsData={visualDefectsData}
        size={size}
        bordered={true}
      />
      <Remark remark={remark} setRemark={setRemark} />
      <Classifications
        classification={classification}
        handleSubmit={handleSubmit}
      />
    </AccordionWrapper>
  );
};
